import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { MouseEventService } from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { setCssProperty } from '../../../cross-team-shared/misc-utils';
import { ViewMode } from '../../../cross-team-shared/state/abstract-sensor-content-state';
import { Dimensions } from '../../../cross-team-shared/types/misc';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { MouseCoordinateService } from '../../shared/mouse-coordinate.service';
import { RasterOverlayState } from '../../shared/state/abstract-sensor-content-state-2d';
import { PanningService } from '../../shared/utils/panning.service';
import {
  BLACK_CODE,
  getRgba,
  TAG_OPTION_ZERO,
  WHITE_CODE,
} from '../state/image-content-state';
import { ImageContentStore } from '../state/image-content-store';
import { TagOptionState } from '../state/tag-option-state';
import { createDistinctColors } from '../utils/color-utils';
import { ColorMapPainter } from './color-map-painter';

@Component({
  selector: 'h3-raster-renderer',
  templateUrl: './raster-renderer.component.html',
  styleUrls: ['./raster-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RasterRendererComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @Input() isOverlay = false;
  @Input() isInsideZoomPanel = false;
  @Input() size?: Dimensions;
  @ViewChild('canvas') private canvasRef!: ElementRef;
  @ViewChild('container') private containerRef!: ElementRef;

  isInEditMode$!: Observable<boolean>;
  transform$!: Observable<string>;
  currentPixelColor$!: Observable<string | undefined>;
  store = inject(ImageContentStore);

  private painter!: ColorMapPainter;

  private mouseEventService = inject(MouseEventService);
  private rawMaskStore: Store<Uint8Array> = this.store('rawMask');
  private partialRawMaskStore = this.store('partialRawMask');
  private mouseCoordinateService = inject(MouseCoordinateService);
  private panningService = inject(PanningService);
  private shellConnector = inject(shellConnectorToken);

  ngOnInit() {
    if (!this.isOverlay && !this.isInsideZoomPanel) {
      const tagOptions = this.createTagOptions();
      this.store.assign({
        tagOptions,
        selectedTagOptionCode: tagOptions[1].code,
      });
    }

    this.isInEditMode$ = this.store('selectedTool').$.pipe(
      map((selectedTool) => selectedTool === 'ColorSelect'),
    );
    if (this.isOverlay) {
      this.initOverlay();
    }

    this.subscribeTo(this.getInput$('size').pipe(filter(isDefined)), (size) => {
      this.mouseCoordinateService.updateScale(size);
    });
  }

  ngAfterViewInit() {
    this.painter = new ColorMapPainter(
      this.canvasRef.nativeElement,
      this.store.state().sensorRendererState.width,
      this.store.state().sensorRendererState.height,
      this,
    );

    this.setUpColorMapSubscription();

    this.subscribeTo(this.rawMaskStore.$, (rawMask) =>
      this.painter.updateMaskAndPaint(rawMask),
    );

    this.subscribeTo(
      this.partialRawMaskStore.$.pipe(filter(isDefined)),
      (partialMask) => this.painter.updatePartialMaskAndPaint(partialMask),
    );

    this.subscribeTo(
      this.mouseEventService.getEvent$('container', 'click'),
      () => {
        this.shellConnector.handleCanvasClick();
      },
    );

    if (this.isInsideZoomPanel) {
      return;
    }

    this.executeNonZoomPanelInitLogic();
  }

  handleMouseEvent(event: MouseEvent) {
    this.mouseEventService.handle({ event, target: 'container' });
  }

  private executeNonZoomPanelInitLogic() {
    this.subscribeTo(this.isInEditMode$, (isInEditMode) => {
      if (isInEditMode) {
        setCssProperty(this.canvasRef, 'cursor', 'pointer');
      } else {
        setCssProperty(this.canvasRef, 'cursor', 'auto');
      }
    });

    if (!this.isOverlay) {
      this.initPanningAndMouseCoordinates();
    }
  }

  private initPanningAndMouseCoordinates() {
    this.panningService.startListening();

    const foreignObject: Element = this.containerRef.nativeElement;
    assert(
      foreignObject.parentElement,
      'The foreignObject always has the svg as parent',
    );
    this.mouseCoordinateService.registerSvgElement({
      foreignObject,
      svg: foreignObject.parentElement,
    });

    this.subscribeTo(
      this.mouseCoordinateService.updateOnMouseMove$(this.destruction$),
    );
  }

  private initOverlay() {
    this.store.getRasterOverlayStore().set(new RasterOverlayState());
    this.rawMaskStore = this.store.getRasterOverlayStore()('rawMask');
    this.partialRawMaskStore =
      this.store.getRasterOverlayStore()('partialRawMask');
    this.transform$ = this.store('mouse')('clientPoint').$.pipe(
      map(
        (clientPoint) => `translate(${clientPoint[0]}px, ${clientPoint[1]}px)`,
      ),
    );
    this.currentPixelColor$ = this.store('mouse')('imagePoint').$.pipe(
      map(() => this.store.getHoveredColor()?.label),
    );
  }

  private createTagOptions() {
    const answerOptions = this.store.state().output.answer_options;
    const colors = createDistinctColors(answerOptions.length);
    const tagOptions = [TAG_OPTION_ZERO];
    answerOptions.forEach((answerOption, i) => {
      tagOptions.push({
        code: +answerOption.name,
        rgb: colors[i],
        // labels are required for raster coding layers
        label: answerOption.label!,
      });
    });
    return tagOptions;
  }

  private setUpColorMapSubscription() {
    const tagOptions$ = this.store('tagOptions').$.pipe(
      distinctUntilChanged(),
      filter(isDefined),
    );

    this.subscribeTo(
      combineLatest([
        tagOptions$,
        this.store('highlightedCode').$,
        this.store('viewMode').$,
        this.store('layerSettings')('opacity').$,
      ]),
      ([tagOptions, highlightedCode, viewMode, layerOpacity]) => {
        this.updateColorMap(
          tagOptions,
          highlightedCode,
          viewMode,
          layerOpacity,
        );
      },
    );
  }

  private updateColorMap(
    tagOptions: TagOptionState[],
    highlightedCode: number | undefined,
    viewMode: ViewMode,
    layerOpacity: number,
  ) {
    const colorMap = new Uint8Array(256 * 4);
    for (let i = tagOptions.length; --i >= 0; ) {
      const tagOption = tagOptions[i];
      const code = tagOption.code;
      const rgba = getRgba(tagOption, highlightedCode, viewMode, layerOpacity);
      const alpha = i > 0 || !this.isOverlay ? rgba[3] : 0;
      colorMap[code * 4] = rgba[0] * alpha;
      colorMap[code * 4 + 1] = rgba[1] * alpha;
      colorMap[code * 4 + 2] = rgba[2] * alpha;
      colorMap[code * 4 + 3] = alpha * 255;
    }
    colorMap[WHITE_CODE * 4] = 127;
    colorMap[WHITE_CODE * 4 + 1] = 127;
    colorMap[WHITE_CODE * 4 + 2] = 127;
    colorMap[WHITE_CODE * 4 + 3] = 127;
    colorMap[BLACK_CODE * 4 + 3] = 127;
    this.painter.updateColorMapAndPaint(colorMap);
  }
}
